import {useEffect, useState} from 'react';
import {SearchApi} from '@/api/searchStatusApi';
import {SearchResultsApi} from '@/api/searchResultsApi';
import JobResults from '@/components/job-results/JobResults';
import SearchStatusBox from '@/components/job-results/SearchStatusBox';
import {onAuthChange} from '@/lib/firebaseAuth'; // Make sure you have this imported correctly
import {motion} from 'framer-motion';
import {useJobSearch} from '@/context/JobSearchContext';
import FailedStatusBox from "@/components/job-results/FailedStatusBox.jsx";
import ResumeViewerPopup from "@/components/popups/ResumeViewerPopup.jsx";
import {fetchResumeFile} from "@/api/resumeApi.js";

export default function JobResultsManager() {
    const [user, setUser] = useState(null); // Local user state from firebaseAuth
    const { lastSearch, setLastSearch } = useJobSearch();
    const [searchResult, setSearchResult] = useState([]); // contain the search results from the API
    const [resumeBlobUrl, setResumeBlobUrl] = useState(null);
    const [isResumePopupOpen, setIsResumePopupOpen] = useState(false);


    // Listen to user login/logout
    useEffect(() => {
        // Start listening to auth changes when the component mounts
        const unsubscribe = onAuthChange((firebaseUser) => {
            setUser(firebaseUser); // Update local user state whenever user signs in or out
            console.log('User signed in:', firebaseUser);
        });

        // Clean up the listener when the component unmounts
        return () => {
            unsubscribe();
        };
    }, []); // Run only once on mount


    // Polling search status
    useEffect(() => {
        if (!user) return; // Only poll if user is logged in AND search is still running

        if (!(lastSearch === null || lastSearch?.task_status === 'pending' || lastSearch.task_status === 'processing')) return;

        const fetchStatus = async () => {
            try {
                const status = await SearchApi.getStatus();
                setLastSearch(status);
            } catch (error) {
                console.error('Error fetching search status:', error);
                setLastSearch({task_status: 'failed'});
            }
        };

        fetchStatus(); // First immediate fetch

        const interval = setInterval(fetchStatus, 2000); // Poll every 2 seconds

        return () => {
            clearInterval(interval); // Clean up polling when component unmounts or when dependencies change
        };
    }, [user, lastSearch?.task_status]);


    // Fetch Result
    useEffect(() => {
        if (!user) return;

        if (lastSearch?.task_status !== 'completed') return;

        const fetchResult = async () => {
            try {
                const data = await SearchResultsApi.getSearchResults(lastSearch.job_id);
                const processedJobs = SearchResultsApi.processJobsData(data);
                setSearchResult(processedJobs);
            } catch (error) {
                console.error('Error fetching search status:', error);
                setSearchResult([]);
            }
        };

        fetchResult(); // First immediate fetch
    }, [user, lastSearch?.task_status]);


    // Prefetch resume PDF blob url
    useEffect(() => {
        let createdUrl;

        const prefetchResume = async () => {
            if (!lastSearch?.resume_file_url) return;

            try {
                // Fetch the resume file asynchronously without blocking rendering
                const blob = await fetchResumeFile();
                createdUrl = URL.createObjectURL(blob); // Create a URL for the blob
                setResumeBlobUrl(createdUrl); // Set the Blob URL in state
            } catch (error) {
                console.error('Failed to prefetch resume file:', error);
            }
        };

        // Perform the fetch but don't block the main render process
        setTimeout(prefetchResume, 500);

        // Clean up the URL when the component unmounts or when the resume file URL changes
        return () => {
            if (createdUrl) {
                URL.revokeObjectURL(createdUrl); // Revoke the object URL when no longer needed
            }
        };
    }, [lastSearch?.resume_file_url]);

    // if user logout
    useEffect(() => {
        if (!user) {
            setLastSearch(null);
            setSearchResult([]);
            setResumeBlobUrl(null);
            setIsResumePopupOpen(false);
        }
    }, [user]);

    // --- Render Logic ---

    // If the user is not logged in => show nothing
    if (!user) {
        return null;
    }

    // If user is logged in but no last search => show nothing
    if (!lastSearch || lastSearch.exists === false) {
        return null;
    }


    const renderContent = () => {
        if (lastSearch.task_status === 'pending' || lastSearch.task_status === 'processing') {
            return (
                <div className="flex items-center justify-center">
                    <SearchStatusBox
                        searchStatusData={lastSearch}
                        setIsResumePopupOpen={setIsResumePopupOpen}
                    />
                </div>
            );
        }

        if (lastSearch.task_status === 'completed') {
            return (
                <JobResults
                    searchParamsData={lastSearch}
                    searchResultData={searchResult}
                    setIsResumePopupOpen={setIsResumePopupOpen}
                />
            );
        }

        if (lastSearch.task_status === 'failed') {
            return (
                <div className="flex items-center justify-center">
                    <FailedStatusBox
                        searchStatusData={lastSearch}
                        setIsResumePopupOpen={setIsResumePopupOpen}
                    />
                </div>
            );
        }

        return null;
    };


    return (
        <div>
            <div id="job-results" className="bg-[#eef5ff] py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div id="job-results" className="bg-[#eef5ff] py-16">
                        <div className="container-fluid px-0 w-full mx-auto">

                            <div className="w-full">
                                {/* Header Section */}
                                <div className="text-center mx-auto max-w-5xl px-4 sm:px-6 mb-8">
                                    <motion.span
                                        initial={{opacity: 0}}
                                        whileInView={{opacity: 1}}
                                        viewport={{once: true}}
                                        className="px-4 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full inline-block mb-4"
                                    >
                                        JOB RESULTS
                                    </motion.span>
                                    <motion.h2
                                        initial={{opacity: 0, y: 20}}
                                        whileInView={{opacity: 1, y: 0}}
                                        viewport={{once: true}}
                                        className="text-3xl md:text-4xl font-bold text-gray-800"
                                    >
                                        Explore Available Jobs
                                    </motion.h2>
                                    <motion.p
                                        initial={{opacity: 0, y: 20}}
                                        whileInView={{opacity: 1, y: 0}}
                                        transition={{delay: 0.1}}
                                        viewport={{once: true}}
                                        className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto"
                                    >
                                        Discover jobs tailored to your skills, experience, and career goals
                                    </motion.p>
                                </div>

                                <div id="job-results-container" className="w-full">
                                    {renderContent()}
                                </div>

                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* Resume Popup always available */}
            <ResumeViewerPopup
                isOpen={isResumePopupOpen}
                onClose={() => setIsResumePopupOpen(false)}
                resumeUrl={resumeBlobUrl}
                resumeFileName={lastSearch.resume_file_url}
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="h-px w-full bg-gradient-to-r from-transparent via-black to-transparent"></div>
            </div>
        </div>
    );
}